import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthenticated, getCustomURL, putAuthenticated } from './api';
import {
  HomeModelsFiltersResponse,
  HomeModelsResponse,
  HomeModelsResponse3D,
  HomeModelsResponseData,
  PutHomeDesignRequest,
  SavedHomeDesigns,
} from '../models/homes.models';
import { useGetCurrentUser } from '../hooks/auth.ts';
const baseUrl = import.meta.env.VITE_HOME_MODEL_API_URL as string;
const stage = import.meta.env.VITE_STAGE === 'prod' ? 'prod' : 'dev';

export const useGetHomeModels = () => {
  const url = `${baseUrl}models/?page_size=10`;
  const { isPending, error, data } = useQuery<HomeModelsResponse>({
    queryKey: ['home-models'],
    queryFn: async () => {
      return getCustomURL<HomeModelsResponse>(url);
    },
  });
  return {
    loading: isPending,
    error,
    data: data?.results || undefined,
  };
};

export const useGetHomeModelById = (id: string) => {
  const url = `${baseUrl}models/${id}/`;
  const { isPending, error, data } = useQuery<HomeModelsResponseData>({
    queryKey: ['home-detail', id],
    queryFn: async () => {
      return getCustomURL<HomeModelsResponseData>(url);
    },
  });
  return {
    loading: isPending,
    error,
    data: data || null,
  };
};
export const useGetHomeModelsFilters = () => {
  const url = `${baseUrl}filters/`;
  const { isPending, error, data } = useQuery<HomeModelsFiltersResponse>({
    queryKey: ['home-models-filters'],
    queryFn: async () => {
      return getCustomURL<HomeModelsFiltersResponse>(url);
    },
  });
  return {
    loading: isPending,
    error,
    data: data || null,
  };
};
export const useGetHomeModelsByIds = (ids: string[]) => {
  const url = `${baseUrl}models/?ids=${ids.join(',')}`;

  const { isPending, error, data, refetch } = useQuery<HomeModelsResponse>({
    queryKey: ['home-models', ids],
    queryFn: async () => {
      return getCustomURL<HomeModelsResponse>(url);
    },
    enabled: ids.length > 0,
  });

  return {
    loading: isPending,
    error,
    data: data || null,
    refetch,
  };
};

export const useGet3dHomeModels = (sku: string) => {
  const url = `home/model/3d-asset?id=${sku}.glb`;
  const { isPending, error, data } = useQuery<HomeModelsResponse3D>({
    queryKey: ['3d-home-models', sku],
    queryFn: async () => {
      return getAuthenticated<HomeModelsResponse3D>(url);
    },
  });
  return {
    loading: isPending,
    error,
    data: data || undefined,
  };
};
export const useUploadImageHomeDesign = () => {
  const url = `https://api.cloudinary.com/v1_1/mcm-${stage}/image/upload`;
  const { data: user } = useGetCurrentUser();
  return useMutation({
    mutationKey: ['home-design-images', user?.userId],
    mutationFn: async (image: FormData) => {
      const res = await fetch(url, {
        method: 'POST',
        body: image,
      });
      return (await res.json()) as never as { public_id: string };
    },
  });
};

export const useSaveHomeDesign = () => {
  const queryClient = useQueryClient();
  const url = `home/design`;
  return useMutation({
    mutationKey: ['homeDesign'],
    mutationFn: async (request: PutHomeDesignRequest) => {
      const body = JSON.stringify(request);
      return putAuthenticated<PutHomeDesignRequest>(url, body);
    },
    onMutate: (request: PutHomeDesignRequest) => {
      queryClient.setQueryData<PutHomeDesignRequest[]>(
        ['homeDesign'],
        (old) => {
          if (old?.find((item) => item.id === request.id)) {
            return old;
          }
          return old ? [...old, request] : [request];
        }
      );
    },
  });
};

export const useGetHomeDesigns = () => {
  const url = `home/design`;
  const { isPending, error, data } = useQuery<SavedHomeDesigns[]>({
    queryKey: ['homeDesign'],
    queryFn: async () => {
      return getAuthenticated<SavedHomeDesigns[]>(url);
    },
  });
  return {
    loading: isPending,
    error,
    data: data || undefined,
  };
};
